import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  TextField as MuiTextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

const emptyPhoneRow = (): string => "";

export default function ContactDetailCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      icon: "",
      contact_name: "",
      phone_numbers: [emptyPhoneRow()],
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        title: values.title,
        icon: values.icon,
        contact_name: values.contact_name,
        phone_numbers: values.phone_numbers.filter((num) => num.trim() !== ""),
        sort_order: values.sort_order,
        status: values.isActive === "Active",
      };

      try {
        let response;
        if (isEdit) {
          response = await request.put("contact-details/" + id, payload);
        } else {
          response = await request.post("contact-details/create", payload);
        }

        if (response?.data?.status) {
          toast.success(
            isEdit ? "Contact updated successfully!" : "Contact created successfully!",
          );
          navigate("/admin/contact-details");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  const handleAddRow = () => {
    formik.setFieldValue("phone_numbers", [...formik.values.phone_numbers, emptyPhoneRow()]);
  };

  const handleRemoveRow = (index: number) => {
    const rows = [...formik.values.phone_numbers];
    rows.splice(index, 1);
    formik.setFieldValue("phone_numbers", rows.length ? rows : [emptyPhoneRow()]);
  };

  const handleRowChange = (index: number, value: string) => {
    const rows = [...formik.values.phone_numbers];
    rows[index] = value;
    formik.setFieldValue("phone_numbers", rows);
  };

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`contact-details/${id}`);
          const item = response.data.data;
          const numbers = Array.isArray(item.phone_numbers)
            ? item.phone_numbers.length
              ? item.phone_numbers.map((n) => String(n))
              : [emptyPhoneRow()]
            : [emptyPhoneRow()];
          formik.setValues({
            title: item.title || "",
            icon: item.icon || "",
            contact_name: item.contact_name || "",
            phone_numbers: numbers,
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Contact" : "Create New Contact"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/contact-details"),
              }}
              primaryButton={{
                label: isEdit ? "Update Contact" : "Create Contact",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="title" label="Title" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="icon" label="Icon" type="text" formik={formik} placeholder="e.g. call" />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="contact_name" label="Contact Name" type="text" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box sx={{ marginTop: "24px" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#1a1f3a" }}>
                    Phone Numbers
                  </Typography>
                  <IconButton
                    onClick={handleAddRow}
                    size="small"
                    sx={{
                      color: "#667eea",
                      border: "1px solid #667eea",
                      borderRadius: "8px",
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {formik.values.phone_numbers.map((num, index) => (
                  <Box key={index} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <MuiTextField
                          fullWidth
                          size="small"
                          label={`Phone Number ${index + 1}`}
                          value={num}
                          onChange={(e) => handleRowChange(index, e.target.value)}
                          sx={{ mb: 0 }}
                        />
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveRow(index)}
                        size="small"
                        sx={{ color: "#ef4444", alignSelf: "center" }}
                        title="Remove row"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}